import React, { useState, useEffect, useCallback } from "react";
import { API } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Home, Tag, CheckCircle2, Percent, RefreshCw } from "lucide-react";

const PERIODS = [
  { key: "today", label: "Today", group: "day" },
  { key: "week", label: "This Week", group: "day" },
  { key: "month", label: "This Month", group: "day" },
];

const fmtPeriod = (p, group) => {
  if (group === "month") {
    const [y, m] = p.split("-");
    return new Date(y, m - 1, 1).toLocaleDateString("en-AU", { month: "short", year: "numeric" });
  }
  const d = new Date(p);
  return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short" });
};

export default function VisitorTraffic() {
  const [range, setRange] = useState("week");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const group = (PERIODS.find((p) => p.key === range) || {}).group || "day";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/visitor-stats?range=${range}&group=${group}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  }, [range, group]);

  useEffect(() => { load(); }, [load]);

  const s = data?.summary || { homepage: 0, pricing_not_subscribed: 0, converted: 0, conversion_rate: 0 };
  const rows = data?.breakdown || [];
  const maxVal = Math.max(1, ...rows.map((r) => Math.max(r.homepage, r.pricing_not_subscribed, r.converted)));

  const StatCard = ({ icon: Icon, label, value, color, testid }) => (
    <Card data-testid={testid}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-5" data-testid="visitor-traffic-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setRange(p.key)}
              data-testid={`visitor-period-${p.key}`}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                range === p.key ? "bg-amber-600 text-white" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="visitor-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <p className="text-xs text-slate-400">
        Anonymous top-of-funnel visitors only — logged-in users are excluded. "Converted" counts paid Stripe subscriptions attributed to a pricing visit.
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Home} label="Homepage Visits" value={s.homepage} color="bg-blue-500" testid="visitor-stat-homepage" />
        <StatCard icon={Tag} label="Pricing Views (not subscribed)" value={s.pricing_not_subscribed} color="bg-purple-500" testid="visitor-stat-pricing" />
        <StatCard icon={CheckCircle2} label="Converted" value={s.converted} color="bg-green-500" testid="visitor-stat-converted" />
        <StatCard icon={Percent} label="Conversion Rate" value={`${s.conversion_rate}%`} color="bg-amber-500" testid="visitor-stat-rate" />
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="visitor-breakdown-table">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="p-3 font-semibold">{group === "month" ? "Month" : "Date"}</th>
                <th className="p-3 font-semibold text-right">Homepage</th>
                <th className="p-3 font-semibold text-right">Pricing (not subscribed)</th>
                <th className="p-3 font-semibold text-right">Converted</th>
                <th className="p-3 font-semibold w-40">Trend</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="5" className="p-8 text-center text-slate-400">No visitor activity in this period yet.</td></tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.period} className="border-t hover:bg-slate-50">
                    <td className="p-3 font-medium text-slate-700">{fmtPeriod(r.period, group)}</td>
                    <td className="p-3 text-right text-blue-600 font-medium">{r.homepage}</td>
                    <td className="p-3 text-right text-purple-600 font-medium">{r.pricing_not_subscribed}</td>
                    <td className="p-3 text-right text-green-600 font-medium">{r.converted}</td>
                    <td className="p-3">
                      <div className="flex items-end gap-0.5 h-8">
                        <span className="bg-blue-500 w-2 rounded-sm" style={{ height: `${(r.homepage / maxVal) * 100}%` }} />
                        <span className="bg-purple-500 w-2 rounded-sm" style={{ height: `${(r.pricing_not_subscribed / maxVal) * 100}%` }} />
                        <span className="bg-green-500 w-2 rounded-sm" style={{ height: `${(r.converted / maxVal) * 100}%` }} />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
